/**
 * QuickStartModal - Main Container Component
 *
 * Orchestrates the 3-screen Quick Start onboarding flow.
 * Manages screen navigation, progress indication, and state.
 */

import React from "react";
import { isFeatureEnabled } from "../../config/features";
import QuickStartScreen1 from "./QuickStartScreen1";
import QuickStartScreen2 from "./QuickStartScreen2";
import QuickStartScreen3 from "./QuickStartScreen3";
import {
  USER_ICON,
  LIBRARY_ICON,
  TIMELINE_ICON,
} from "../../constants/icons";
import "./QuickStart.css";

/**
 * Progress Indicator Component
 */
const SCREEN_ICONS = {
  1: USER_ICON,
  2: LIBRARY_ICON,
  3: TIMELINE_ICON,
};

const ProgressIndicator = ({ currentScreen, screensNeeded }) => {
  const allScreens = [1, 2, 3];

  return (
    <div className="quick-start-progress">
      {allScreens.map((screen, index) => {
        const isNeeded = screensNeeded.includes(screen);
        const isActive = screen === currentScreen;
        const isCompleted = screensNeeded.indexOf(currentScreen) > screensNeeded.indexOf(screen);
        const isLast = index === allScreens.length - 1;
        const icon = SCREEN_ICONS[screen];

        if (!isNeeded) {
          return (
            <React.Fragment key={screen}>
              <div
                className="progress-step"
                style={{ opacity: 0.3 }}
                title={`Step ${screen} (not needed)`}
              >
                {icon}
              </div>
              {!isLast && <div className="progress-line" style={{ opacity: 0.3 }} />}
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={screen}>
            <div
              className={`progress-step ${isActive ? "active" : ""} ${
                isCompleted ? "completed" : ""
              }`}
              title={`Step ${screen}`}
            >
              {isCompleted ? icon : icon}
            </div>
            {!isLast && (
              <div className={`progress-line ${isCompleted ? "completed" : ""}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/**
 * Loading State
 */
const LoadingState = () => (
  <div className="quick-start-loading">
    <div className="loading-spinner" />
    <p>Checking your profile...</p>
  </div>
);

/**
 * Error State
 */
const ErrorState = ({ message, onRetry }) => (
  <div className="quick-start-error">
    <p>⚠️ {message}</p>
    <button className="btn btn-secondary" onClick={onRetry}>
      Try Again
    </button>
  </div>
);

/**
 * Quick Start Modal Component
 *
 * Props:
 * - quickStart: Object from useQuickStart() hook containing state and handlers
 * - user: Current user object
 * - onUserUpdate: Callback to refresh the app-wide user object after a profile change
 */
const QuickStartModal = ({ quickStart, user, onUserUpdate }) => {
  // If feature is disabled, don't render
  if (!isFeatureEnabled("QUICK_START_ENABLED")) {
    return null;
  }

  // If not open, don't render
  if (!quickStart.isOpen) {
    return null;
  }

  const screensNeeded = quickStart.status?.screens_needed || [];

  // Handle close
  const handleClose = () => {
    quickStart.close();
  };

  return (
    <div className="quick-start-overlay" onClick={handleClose}>
      <div className="quick-start-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="quick-start-header">
          <h2 className="quick-start-title">Quick Start</h2>
          <button
            className="quick-start-close"
            onClick={handleClose}
            aria-label="Close Quick Start"
          >
            ×
          </button>
        </div>

        {/* Info Text */}
        <div className="quick-start-info">
          <span className="quick-start-info-icon">💡</span>
          <span className="quick-start-info-text">
            You can relaunch Quick Import anytime from your Profile section
          </span>
          <span className="quick-start-info-icon pista-profile-icon">
            {user?.username?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
          </span>
        </div>

        {/* Progress Indicator */}
        <ProgressIndicator
          currentScreen={quickStart.currentScreen}
          screensNeeded={screensNeeded}
        />

        {/* Content Area */}
        <div className="quick-start-content">
          {quickStart.isLoading && !quickStart.status ? (
            <LoadingState />
          ) : quickStart.error && !quickStart.status ? (
            <ErrorState
              message={quickStart.error}
              onRetry={() => {
                quickStart.resetCheck();
                quickStart.checkAndShow();
              }}
            />
          ) : (
            <>
              {/* Screen 1: BGG ID */}
              {quickStart.currentScreen === 1 && screensNeeded.includes(1) && (
                <QuickStartScreen1
                  bggId={quickStart.bggId}
                  setBggId={quickStart.setBggId}
                  isImporting={quickStart.isImporting}
                  importProgress={quickStart.importProgress}
                  importResult={quickStart.importResult}
                  onSubmit={quickStart.handleBggIdSubmit}
                  onOptOut={quickStart.handleBggIdOptOut}
                  onSkip={quickStart.skipScreen}
                  username={quickStart.username}
                  setUsername={quickStart.setUsername}
                  isSavingUsername={quickStart.isSavingUsername}
                  usernameError={quickStart.usernameError}
                  onUsernameSave={async (value) => {
                    const ok = await quickStart.handleUsernameSave(value);
                    if (ok) onUserUpdate?.();
                  }}
                />
              )}

              {/* Screen 2: Collection Add */}
              {quickStart.currentScreen === 2 && screensNeeded.includes(2) && (
                <QuickStartScreen2
                  selectedGames={quickStart.selectedGames}
                  onAddGame={quickStart.addGameToSelection}
                  onRemoveGame={quickStart.removeGameFromSelection}
                  onUpdateOwnership={quickStart.updateGameOwnership}
                  onUpdateHasPlayed={quickStart.updateGameHasPlayed}
                  onUpdatePreference={quickStart.updateGamePreference}
                  onBulkAdd={quickStart.bulkAddToCollection}
                  onSkip={quickStart.skipScreen}
                  onBack={quickStart.prevScreen}
                  isLoading={quickStart.isLoading}
                />
              )}

              {/* Screen 3: Session Logging */}
              {quickStart.currentScreen === 3 && screensNeeded.includes(3) && (
                <QuickStartScreen3
                  sessionOption={quickStart.sessionOption}
                  setSessionOption={quickStart.setSessionOption}
                  sessionGames={quickStart.sessionGames}
                  onAddGame={quickStart.addGameToSession}
                  onRemoveGame={quickStart.removeGameFromSession}
                  onUpdateResult={quickStart.updateGameResult}
                  onCreateFromCollection={quickStart.createSessionsFromCollection}
                  onBulkCreateSessions={quickStart.bulkCreateSessions}
                  onSkip={quickStart.skipScreen}
                  onBack={quickStart.prevScreen}
                  isLoading={quickStart.isLoading}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickStartModal;
